import { useCallback, useMemo } from "react";
import {
    getLeafGroupLabel,
    MAX_ACCOUNT_GROUP_DEPTH,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { RowDropPosition } from "@design-system/ViewTable/ViewTable.lib";
import { DraftAccount, DraftGroup } from "../draftState";
import { MoveRowArgs, MoveRowsArgs } from "../draftOperations";
import { ScopeInsertPosition } from "../draftSortOrder";
import { GetRowCapabilities } from "../editorCapabilities";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    DropBlockReason,
    DropScope,
    deriveDropTarget,
    evaluateDrop,
    isDropAllowed,
    isNestedRowDroppable,
} from "./nestedDragRules";
import {
    evaluateRowsDrop,
    resolveMultiDragTopLevelRows,
} from "./nestedMultiDragRules";
import {
    commitMergedRowDrop,
    commitMergedRowsDrop,
    MergedDropResult,
} from "./mergedDrop";
import { collectNestedActiveDragRowIds } from "./collectNestedActiveDragRowIds";
import { isRowPending } from "./nestedPendingAdds";
import { ChartOfAccountsTemplateNestedColumn } from "./nestedViewConfig";
import { CoaEditorCommand } from "./coaEditorCommand";

interface UseNestedEditorDragAndDropParams {
    accounts: DraftAccount[];
    groups: DraftGroup[];
    getRowCapabilities: GetRowCapabilities;
    moveRow: (args: MoveRowArgs) => void;
    moveRows: (args: MoveRowsArgs) => void;
    // A merged drop mints the destination groups it lands under (workspace only).
    createGroup: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        name: string;
        position?: ScopeInsertPosition;
    }) => string;
    // Multi-select drag: whole set when the active row is one of ≥2 selected.
    selectedDraggableRows: NestedEditorRow[];
    nestedRows: NestedEditorRow[];
    // undefined: templates 1:1 tree. Array (incl []): workspace merge view —
    // never pass a display-key id to moveRow/moveRows.
    displayedEntityIds: number[] | undefined;
    // Search/filter hides siblings a reorder would still move.
    disabled: boolean;
    pendingDraftIds: ReadonlySet<string>;
    onOperation?: (command: CoaEditorCommand) => void;
}

interface NestedRowDragAndDrop {
    disabled: boolean;
    enablePositionalDrop: true;
    canDragRow: (row: NestedEditorRow) => boolean;
    canNestRow: (row: NestedEditorRow) => boolean;
    canDropOnRow: (row: NestedEditorRow) => boolean;
    validateDrop: (args: {
        activeRow: NestedEditorRow;
        overRow: NestedEditorRow;
        dropPosition: RowDropPosition;
    }) => boolean;
    onRowDrop: (args: {
        activeRow: NestedEditorRow;
        overRow: NestedEditorRow;
        dropPosition: RowDropPosition;
    }) => void;
    // Includes group descendants so ViewTable lifts the whole moving set.
    getActiveDragRowIds: (activeRow: NestedEditorRow) => string[];
    dragHandleColumnId: ChartOfAccountsTemplateNestedColumn;
}

const MOVE_SELECTED_ROWS_BLOCKED_MESSAGE = "Can't move the selected items here";

const resolveBlockedMessage = (
    reason: DropBlockReason,
    draggedRow: NestedEditorRow,
    targetScope: DropScope,
): string => {
    switch (reason) {
        case "differentType": {
            const lockedType =
                draggedRow.account?.type ??
                draggedRow.group?.type ??
                targetScope.type;
            return `Can't move "${draggedRow.name}" here — it must stay under ${getLeafGroupLabel(
                lockedType,
            )}`;
        }
        case "lockedDescendant":
            return `Can't move "${draggedRow.name}" here — it contains an account whose Type is locked`;
        case "depthExceeded":
            return `Can't nest "${draggedRow.name}" beyond ${MAX_ACCOUNT_GROUP_DEPTH} account-group levels`;
        case "cycle":
            return `Can't move "${draggedRow.name}" into one of its own subgroups`;
        case "duplicateName":
            return draggedRow.kind === "group"
                ? `A group named '${draggedRow.name}' already exists here.`
                : `An account named "${draggedRow.name.trim()}" already exists here`;
        default: {
            // Missing DropBlockReason must fail compile, not toast empty.
            const exhaustiveReason: never = reason;
            return exhaustiveReason;
        }
    }
};

const resolveBlockedSetMessage = ({
    reason,
    rows,
    targetScope,
}: {
    reason: DropBlockReason;
    rows: NestedEditorRow[];
    targetScope: DropScope;
}): string =>
    rows.length === 1
        ? resolveBlockedMessage(reason, rows[0], targetScope)
        : MOVE_SELECTED_ROWS_BLOCKED_MESSAGE;

const toMoveRowsMembers = (rows: NestedEditorRow[]): MoveRowsArgs["rows"] =>
    rows.map((row) => ({
        id: row.id,
        kind: row.kind === "group" ? "group" : "account",
    }));

export const useNestedEditorDragAndDrop = ({
    accounts,
    groups,
    getRowCapabilities,
    moveRow,
    moveRows,
    createGroup,
    selectedDraggableRows,
    nestedRows,
    displayedEntityIds,
    disabled,
    pendingDraftIds,
    onOperation,
}: UseNestedEditorDragAndDropParams): NestedRowDragAndDrop => {
    const { error: errorToast } = useToaster();

    const canDragRow = useCallback(
        (row: NestedEditorRow) =>
            (row.kind === "account" || row.kind === "group") &&
            !isRowPending(row, pendingDraftIds),
        [pendingDraftIds],
    );

    // Accounts are leaves; ViewTable uses this for thirds vs halves drop zones.
    const canNestRow = useCallback(
        (row: NestedEditorRow) => row.kind !== "account",
        [],
    );

    // False redirects to the nearest droppable sibling instead of reverting.
    const canDropOnRow = useCallback(
        (row: NestedEditorRow) =>
            isNestedRowDroppable(row) && !isRowPending(row, pendingDraftIds),
        [pendingDraftIds],
    );

    // Evaluates the display row against unscoped groups, so the per-entity
    // commit path may still toast-refuse a drop the indicator allowed.
    const validateDrop = useCallback(
        ({
            activeRow,
            overRow,
            dropPosition,
        }: {
            activeRow: NestedEditorRow;
            overRow: NestedEditorRow;
            dropPosition: RowDropPosition;
        }): boolean => {
            const moveRowsSet = resolveMultiDragTopLevelRows({
                activeRow,
                selectedDraggableRows,
                groups,
            });
            if (!moveRowsSet) {
                return isDropAllowed({
                    draggedRow: activeRow,
                    overRow,
                    dropPosition,
                    groups,
                    accounts,
                    getRowCapabilities,
                });
            }
            const target = deriveDropTarget({
                overRow,
                dropPosition,
                draggedRow: activeRow,
            });
            if (!target) {
                return false;
            }
            return evaluateRowsDrop({
                draggedRows: moveRowsSet,
                targetScope: target.scope,
                groups,
                accounts,
                getRowCapabilities,
            }).ok;
        },
        [accounts, groups, getRowCapabilities, selectedDraggableRows],
    );

    const emitMove = useCallback(
        (result: MergedDropResult) => {
            // A drop where no business had the over-row moves nothing.
            if (!result.ok || result.movedRows.length === 0) {
                return;
            }
            onOperation?.({
                type: "move",
                rows: result.movedRows,
                createdGroups: result.createdGroups,
            });
        },
        [onOperation],
    );

    // Templates 1:1 tree: the display-key row id IS the draft id.
    const handleTemplatesDrop = useCallback(
        ({
            activeRow,
            moveRowsSet,
            target,
        }: {
            activeRow: NestedEditorRow;
            moveRowsSet: NestedEditorRow[] | null;
            target: NonNullable<ReturnType<typeof deriveDropTarget>>;
        }) => {
            if (moveRowsSet) {
                const result = evaluateRowsDrop({
                    draggedRows: moveRowsSet,
                    targetScope: target.scope,
                    groups,
                    getRowCapabilities,
                    accounts,
                });
                if (!result.ok) {
                    errorToast(
                        resolveBlockedSetMessage({
                            reason: result.reason,
                            rows: moveRowsSet,
                            targetScope: target.scope,
                        }),
                    );
                    return;
                }
                moveRows({
                    rows: toMoveRowsMembers(moveRowsSet),
                    entityId: target.scope.entityId,
                    targetType: target.scope.type,
                    targetParentGroupId: target.scope.parentGroupId,
                    overRefId: target.overRefId,
                    position: target.position,
                });
                return;
            }
            const result = evaluateDrop({
                draggedRow: activeRow,
                targetScope: target.scope,
                groups,
                getRowCapabilities,
                accounts,
            });
            if (!result.ok) {
                errorToast(
                    resolveBlockedMessage(
                        result.reason,
                        activeRow,
                        target.scope,
                    ),
                );
                return;
            }
            moveRow({
                draggedId: activeRow.id,
                draggedKind: activeRow.kind === "group" ? "group" : "account",
                entityId: target.scope.entityId,
                targetType: target.scope.type,
                targetParentGroupId: target.scope.parentGroupId,
                overRefId: target.overRefId,
                position: target.position,
            });
        },
        [accounts, groups, getRowCapabilities, moveRow, moveRows, errorToast],
    );

    const handleRowDrop = useCallback(
        ({
            activeRow,
            overRow,
            dropPosition,
        }: {
            activeRow: NestedEditorRow;
            overRow: NestedEditorRow;
            dropPosition: RowDropPosition;
        }) => {
            if (activeRow.kind !== "account" && activeRow.kind !== "group") {
                return;
            }
            const moveRowsSet = resolveMultiDragTopLevelRows({
                activeRow,
                selectedDraggableRows,
                groups,
            });
            const target = deriveDropTarget({
                overRow,
                dropPosition,
                draggedRow: activeRow,
            });
            if (!target) {
                // Toast for parity with other blocked drops; the indicator already showed blocked.
                errorToast(
                    moveRowsSet && moveRowsSet.length > 1
                        ? MOVE_SELECTED_ROWS_BLOCKED_MESSAGE
                        : `Can't move "${activeRow.name}" here`,
                );
                return;
            }
            if (displayedEntityIds === undefined) {
                handleTemplatesDrop({ activeRow, moveRowsSet, target });
                return;
            }
            if (moveRowsSet) {
                const result = commitMergedRowsDrop({
                    movingRows: moveRowsSet,
                    overRow,
                    dropPosition,
                    accounts,
                    groups,
                    displayedEntityIds,
                    getRowCapabilities,
                    deps: { createGroup, moveRows },
                });
                if (!result.ok) {
                    errorToast(
                        resolveBlockedSetMessage({
                            reason: result.reason,
                            rows: moveRowsSet,
                            targetScope: target.scope,
                        }),
                    );
                    return;
                }
                emitMove(result);
                return;
            }
            const result = commitMergedRowDrop({
                movingRow: activeRow,
                overRow,
                dropPosition,
                accounts,
                groups,
                getRowCapabilities,
                deps: { createGroup, moveRows },
            });
            if (!result.ok) {
                errorToast(
                    resolveBlockedMessage(
                        result.reason,
                        activeRow,
                        target.scope,
                    ),
                );
                return;
            }
            emitMove(result);
        },
        [
            accounts,
            groups,
            getRowCapabilities,
            moveRows,
            createGroup,
            displayedEntityIds,
            selectedDraggableRows,
            errorToast,
            handleTemplatesDrop,
            emitMove,
        ],
    );

    const getActiveDragRowIds = useCallback(
        (activeRow: NestedEditorRow) =>
            collectNestedActiveDragRowIds({
                activeRow,
                selectedDraggableRows,
                nestedRows,
                groups,
            }),
        [selectedDraggableRows, nestedRows, groups],
    );

    return useMemo(
        () => ({
            disabled,
            // Only this editor uses pointer-based before/on/after; other tables leave it off.
            enablePositionalDrop: true,
            canDragRow,
            canNestRow,
            canDropOnRow,
            validateDrop,
            onRowDrop: handleRowDrop,
            getActiveDragRowIds,
            dragHandleColumnId: ChartOfAccountsTemplateNestedColumn.DRAG,
        }),
        [
            disabled,
            canDragRow,
            canNestRow,
            canDropOnRow,
            validateDrop,
            handleRowDrop,
            getActiveDragRowIds,
        ],
    );
};
